import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { atom, useAtom } from 'jotai';
import { FC, ReactElement, ReactNode, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

type AlertAction = {
  label: string;
  onClick?: () => void | Promise<void>;
  variant?: 'default' | 'destructive';
};

type AlertItem = {
  id: string;
  title: string;
  content: string | ReactElement;
  actions?: AlertAction[];
};

type AlertApi = {
  openAlert: (params: { title: string; content: string | ReactElement; actions?: AlertAction[] }) => void;
  dismissActiveAlert: () => void;
  dismissAllAlerts: () => void;
};

const alertStackAtom = atom<AlertItem[]>([]);

interface ProviderProps {
  children?: ReactNode;
}

const AlertProvider: FC<ProviderProps> = ({ children }) => {
  const [alerts, setAlerts] = useAtom(alertStackAtom);
  // App-wide (UXB5): Cancel and the default OK in the UI language, like every caller's own action label.
  const { t } = useTranslation('common');

  const handleCloseById = useCallback(
    (id: string) => setAlerts((prev) => prev.filter((a) => a.id !== id)),
    [setAlerts],
  );

  useEffect(() => {
    if (alerts.length === 0) {
      document.body.style.setProperty('pointer-events', 'auto');
    } else {
      document.body.style.setProperty('pointer-events', 'none');
    }
  }, [alerts]);

  return (
    <>
      {children}
      {alerts.map((item) => (
        <AlertDialog
          key={item.id}
          open
          onOpenChange={(open) => {
            if (!open) handleCloseById(item.id);
          }}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>{item.title}</AlertDialogTitle>
              {typeof item.content === 'string' ? (
                <AlertDialogDescription>{item.content}</AlertDialogDescription>
              ) : (
                item.content
              )}
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel onClick={() => handleCloseById(item.id)}>{t('alert.cancel')}</AlertDialogCancel>
              {(item.actions || [{ label: t('alert.ok') }]).map((action, index) => (
                <AlertDialogAction
                  key={`${item.id}-action-${index}`}
                  variant={action.variant}
                  onClick={async () => {
                    try {
                      await action.onClick?.();
                    } finally {
                      handleCloseById(item.id);
                    }
                  }}>
                  {action.label}
                </AlertDialogAction>
              ))}
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      ))}
    </>
  );
};

export const useAlert = (): AlertApi => {
  const [, setAlerts] = useAtom(alertStackAtom);

  const openAlert = useCallback(
    (params: { title: string; content: string | ReactElement; actions?: AlertAction[] }) => {
      const id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random()}`;
      setAlerts((prev) => [...prev, { id, ...params }]);
    },
    [setAlerts],
  );

  const dismissActiveAlert = useCallback(
    () => setAlerts((prev) => (prev.length > 0 ? prev.slice(0, -1) : prev)),
    [setAlerts],
  );

  const dismissAllAlerts = useCallback(() => setAlerts([]), [setAlerts]);

  return { openAlert, dismissActiveAlert, dismissAllAlerts };
};

export default AlertProvider;
